import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Checkbox,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { SeguimientoCausa, EstadoCausa } from './types';
import { createSeguimiento } from '../../services/causasApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  expediente: string;
  onGuardar: (s: SeguimientoCausa) => void;
}

const ESTADOS: EstadoCausa[] = ['Abierta', 'En revisión', 'Archivada / Cerrada'];

export default function ModalSeguimiento({
  isOpen,
  onClose,
  expediente,
  onGuardar,
}: Props) {
  const toast = useToast();

  const [fecha, setFecha] = useState<string>('');
  const [nota, setNota] = useState<string>('');
  const [estadoNuevoSel, setEstadoNuevoSel] = useState<EstadoCausa | ''>(''); // 👈 EstadoCausa o vacío
  const [firmado, setFirmado] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      // Fecha/hora actual en formato datetime-local
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const isoLocal = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
      )}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
      setFecha(isoLocal);
      setNota('');
      setEstadoNuevoSel('');
      setFirmado(false);
    }
  }, [isOpen]);

 
  const handleGuardar = () => {
    if (!expediente) {
      toast({
        title: 'Falta expediente',
        description: 'No se puede guardar seguimiento sin expediente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!fecha) {
      toast({
        title: 'Falta fecha',
        description: 'Indica la fecha y hora del seguimiento.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Conversión segura a EstadoCausa | undefined
    const estadoValue: EstadoCausa | undefined =
      estadoNuevoSel === '' ? undefined : estadoNuevoSel;

    const payload: SeguimientoCausa = {
      id: expediente,
      causaExpediente: expediente,
      fecha,
      nota: nota || undefined,
      estado_nuevo: estadoValue, // ✅ Tipado correcto
      firmado,
    };

    onGuardar(payload);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          fontSize="2xl" 
          bgImage="url('/img/bg.webp')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          color="white"
          textShadow="2px 2px 4px rgba(0,0,0,0.7)"
          fontWeight="bold"
          borderTopRadius="md"
          position="relative"
        >
          Nuevo seguimiento — {expediente || '—'}
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
        <ModalBody>
          <FormControl isRequired mb={3}>
            <FormLabel>Fecha y hora</FormLabel>
            <Input
              type="datetime-local"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>Estado nuevo (opcional)</FormLabel>
            <Select
              placeholder="Selecciona un estado"
              value={estadoNuevoSel}
              onChange={(e) =>
                setEstadoNuevoSel(e.target.value as EstadoCausa | '')
              }
            >
              {ESTADOS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>Nota</FormLabel>
            <Textarea
              placeholder="Detalles del seguimiento"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />
          </FormControl>

          <FormControl display="flex" alignItems="center">
            <Checkbox
              isChecked={firmado}
              onChange={(e) => setFirmado(e.target.checked)}
            >
              Firmado
            </Checkbox>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button bg="#5b182b" color="white" _hover={{ bg: "#8a1f35" }} mr={3} onClick={handleGuardar}>
            Guardar
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
